import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  TablePagination,
  TableSortLabel,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { StyledTableRow } from "../../../styles/Shipmentstyles";
import { InventoryListing } from "../../../models/inventory/InventoryListing";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import { SortType } from "../../../models/SortType";
import CustomTypography from "../common/form/CustomTypography";

interface InventoryListingTableProps {
  maxHeight: number;
  inventories: InventoryListing[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  // handleNavigateById: (id: number) => void;
  minHeight?: number;
}

const InventoryListingTable = ({
  maxHeight,
  inventories,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  minHeight = window.innerHeight - 350,
}: // handleNavigateById,
InventoryListingTableProps) => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ width: "100%" }}>
              <AppTableHeaderCell sx={{ width: 110, minWidth: 110, textAlign: "center" }} >
                <TableSortLabel
                  active={sortBy === "id"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "id",
                      sortBy === "id" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => <span>{renderSortIcon("id")}</span>}
                  sx={{ fontSize: "14px"  }}
                >
                  ID
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell title="LABEL" fontSize="14px" />
              <AppTableHeaderCell title="ORDER ID" fontSize="14px" />
              <AppTableHeaderCell title="CUSTOMER NAME" fontSize="14px" />
              <AppTableHeaderCell sx={{ maxWidth: 130 }}  title="HAS COUPON" fontSize="14px" />
              <AppTableHeaderCell sx={{ maxWidth: 130 }} title="HAS ONELINK" fontSize="14px" />
              <AppTableHeaderCell title="REDEMPTION CODE" fontSize="14px" />
              <AppTableHeaderCell  sx={{ maxWidth: 130 }} title="IS MASTER" fontSize="14px" />
              <AppTableHeaderCell sx={{ maxWidth: 130 }} title="ACTIVE" fontSize="14px" />

              <AppTableHeaderCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortBy === "created_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "created_date",
                      sortBy === "created_date" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("created_date")}</span>
                  )}
                  sx={{ fontSize: "14px" }}
                >
                  CREATED DATE
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortBy === "modified_date"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "modified_date",
                      sortBy === "modified_date" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("modified_date")}</span>
                  )}
                  sx={{ fontSize: "14px" }}
                >
                  MODIFIED DATE
                </TableSortLabel>
              </AppTableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {inventories.map((inventory) => (
              <StyledTableRow key={inventory.id}>
                <TableCell
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    textAlign: "center",
                    fontSize: 14,
                  }}
                >
                  <Box
                    onClick={() => navigate(`/inventory/${inventory.id}/packs`)}
                  >
                    {inventory.id}
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.label || "-"}
                </TableCell>

                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.order_ids && inventory.order_ids.length > 0
                    ? inventory.order_ids.join(", ")
                    : "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.customer_name || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.has_coupon ? "Yes" : "No"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.has_onelink ? "Yes" : "No"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.redemption_code_global || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.is_master ? "Yes" : "No"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {inventory.active ? "Yes" : "No"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {new Date(inventory.created_date).toLocaleString()}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                  {new Date(inventory.modified_date).toLocaleString()}
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        {inventories.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 15,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="body2"
              fontWeight={700}
            >
              NO INVENTORY FOUND!
            </CustomTypography>
          </Box>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={page_no - 1}
        onPageChange={(event, page_no) => onPageChange(event, page_no + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
      />
    </>
  );
};

export default InventoryListingTable;
